<template>
  <component
    :is="componentType"
    class="VisionTag"
    :class="[
      {
        'VisionTag--outlined': outlined,
        'VisionTag--gradient-text': withGradientText
      },
      `VisionTag--theme-${theme || 'white'}`,
      `VisionTag--size-${size}`,
    ]"
    :href="tagLink"
    :style="delay ? `--tag-animation-delay: ${delay}s` : null"
  >
    <span class="VisionTag__text">
      <span v-if="!$slots.default">
        {{ text }}
      </span>
      <slot
        v-else-if="$slots.default"
      />
    </span>
  </component>
</template>

<script>
import { VisionTagConfig } from './config';

export default {
  name: 'VisionTag',

  props: {
    tag: {
      type: Object,
      default: () => ({}),
    },
    text: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: 'default',
      validator(value) {
        return VisionTagConfig.themes.includes(value) || !value
      },
    },
    withGradientText: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'large',
      validator(value) {
        return ['x-small', 'small', 'medium', 'large'].includes(value)
      },
    },
    outlined: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      delay: 0
    }
  },

  computed: {
    componentType() {
      return this.tag && this.tag.url ? 'a' : 'span'
    },

    tagLink() {
      return this.tag && this.tag.url ? this.tag.url : null
    }
  },

  mounted() {
    if (this.theme === 'promo') this.delay = Math.floor(Math.random() * -8)
  }
}
</script>

<style lang="postcss">
.VisionTag {
  --vision-tag-padding-y: 0.75rem;
  --vision-tag-padding-x: 1.25rem;

  @apply inline-flex
    text-center
    rounded-full;
    background-color: var(--vision-tag-background-color, theme('colors.white'));

  padding: var(--vision-tag-padding-y) var(--vision-tag-padding-x);

  @screen md {
    --vision-tag-padding-x: 1.5rem;

    @apply px-6;
  }
}

.VisionTag--outlined {
  @apply border
    leading-none
    py-1.5 px-3;
    border-color: var(--vision-tag-border-color, var(--vision-tag-background-color, theme('colors.grey.DEFAULT')));
}

.VisionTag__text {
  @apply inline-block font-fragment-mono whitespace-nowrap;

  color: var(--vision-tag-text-color, theme('colors.off-black'));
  font-size: var(--vision-tag-font-size, themes('fontSize.xs'));

}

.VisionTag--gradient-text .VisionTag__text {
  @supports (-webkit-background-clip: text) {
    @apply t-gradient;
  }
}

/* SIZE */
.VisionTag--size-medium {
  @apply py-2.5 px-3.5;

  .VisionTag__text {
    @apply block leading-none;

    font-size: var(--vision-tag-font-size, themes('fontSize.xxs'));
  }
}

.VisionTag--size-small {
  @apply py-1 px-2;

  .VisionTag__text {
    @apply block leading-none;

    font-size: var(--vision-tag-font-size, 10px);
  }
}

.VisionTag--size-x-small {
  @apply py-1 px-2;

  .VisionTag__text {
    @apply block leading-none;

    font-size: var(--vision-tag-font-size, 9px);
  }
}

/* THEMES */
.VisionTag--theme-default {
  .VisionTag__text {
    @supports (-webkit-background-clip: text) {
      @apply t-gradient;
    }
  }
}

.VisionTag--theme-white {
  background-color: var(--vision-tag-background-color, theme('colors.white'));

  .VisionTag__text {
    @apply bg-none;
    color: var(--vision-tag-text-color, theme('colors.black'));

    -webkit-text-fill-color: unset;
  }
}

.VisionTag--theme-gradient {
  @apply bg-gradient;

  .VisionTag__text {
    color: var(--vision-tag-text-color, theme('colors.white'));

    -webkit-text-fill-color: unset;
  }
}

.VisionTag--theme-grey {
  background-color: var(--vision-tag-background-color, theme('colors.grey-light'));
}

.VisionTag--theme-gold {
  background-image: var(--gradient-gold);
}

.VisionTag--theme-orange {
  background-color: var(--vision-tag-background-color, #ff5f1f);
}

.VisionTag--theme-gold,
.VisionTag--theme-orange {
  .VisionTag__text {
    @apply bg-none;
    color: var(--vision-tag-text-color, theme('colors.white'));

    -webkit-text-fill-color: unset;
  }
}

.VisionTag--theme-purple-pink {
  background-image: var(--gradient-purple-pink);

  .VisionTag__text {
    @apply bg-none;
    color: var(--vision-tag-text-color, theme('colors.white'));
    -webkit-text-fill-color: unset;
  }
}

.VisionTag--theme-blur {
  @apply backdrop-blur-md border border-white border-opacity-25;
  background-color: rgba(0,0,0,0.04);

  .VisionTag__text {
    @apply bg-none;
    color: var(--vision-tag-text-color, theme('colors.white'));

    -webkit-text-fill-color: unset;
  }
}

.VisionTag--theme-promo {
  @apply promo-gradient-border promo-gradient-border-animated;
  animation-delay: var(--tag-animation-delay);

  .VisionTag__text {
    @apply promo-t-gradient promo-t-gradient-animated;
    animation-delay: var(--tag-animation-delay);
  }
}
</style>
